















import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { PackageSortOrder, PackageSummary } from "../../../generated/contracts";
import Api from "../../../core/api/Api";
import { TableHeader } from "../../atoms/table/tableModels";
import { dateTimeFilter } from "../../filters/dateTimeFilters";
import router from "../../router";
import TableViewControlModel, { ToolbarItem } from "../../components/table-view/tableViewModels";
import constants from "../../../core/constants";
import { statusToDisplayNameAndColor, toDomainSortBy } from "../shared/tableHelper";
import TableView from "../../components/table-view/TableView.vue";

interface PackageSummaryTableHeader extends TableHeader {
    key: keyof (PackageSummary);
}

@Component({
    components: { TableView }
})
export default class DeliveryView extends Vue {
    @Prop() private id!: string;
    @Prop() private tab!: string;

    private loadFromUrl = this.tab === router.currentRoute.query["tab"];
    private initialSortOrder: keyof (PackageSummary) = router.currentRoute.query["sortOrder"] && this.loadFromUrl ?
                (router.currentRoute.query["sortOrder"] as keyof (PackageSummary))! :
                "status";
    private initialSortBy = router.currentRoute.query["sortByAsc"] && this.loadFromUrl ?
                router.currentRoute.query["sortByAsc"] === "true" :
                true;
    private initialSearchTerm = router.currentRoute.query["searchTerm"] && this.loadFromUrl ?
                router.currentRoute.query["searchTerm"] :
                null;

    private tableControlModel: TableViewControlModel = {
        fromDate: null,
        toDate: null,
        searchTerm: this.initialSearchTerm,
        sort: {
            sortOrder: this.initialSortOrder,
            sortByAscending: this.initialSortBy
        }
    };
    private loadingOrderBy = false;
    private loadingMore = false;
    private packageSummaries: PackageSummary[] = [];

    private mounted() {
        this.load(0, true);
    }

    private get tableHeader(): PackageSummaryTableHeader[] {
        return [
            { key: "ssccNumber", title: "SSCC", size: 20 },
            { key: "packageGroupSsccNumber", title: "Sampak", size: 15 },
            { key: "lineItemCount", title: "Antal varer", size: 10 },
            { key: "deliveryTimeUtc", title: "Leveret", size: 21 },
            { key: "warehouse", title: "Lager", size: 12 },
            { key: "warehouseArea", title: "Lomnr.", size: 11 },
            { key: "packageType", title: "Kolli type", size: 11 },
            { key: "status", title: "Status", size: 11 }
        ];
    }

    private rowClicked(row: PackageSummary) {
        router.push({ name: "Package", params: { id: row.ssccNumber } });
    }

    private tableControlModelChanged(tableControlData: TableViewControlModel) {
        this.tableControlModel = tableControlData;
        this.loadingOrderBy = false;
        this.load();
    }

    private async load(skip = 0, firstLoad = false, take = constants.NoOfRowsAtATime) {
        this.loadingMore = true;
        try {
            const sortByAsc = router.currentRoute.query["sortByAsc"] && firstLoad && this.loadFromUrl ?
                router.currentRoute.query["sortByAsc"] === "true" :
                this.tableControlModel.sort.sortByAscending;

            const sortOrder = router.currentRoute.query["sortOrder"] && firstLoad && this.loadFromUrl ?
                (router.currentRoute.query["sortOrder"] as keyof (PackageSummary))! :
                (this.tableControlModel.sort.sortOrder as keyof (PackageSummary))!;

            const searchTerm = router.currentRoute.query["searchTerm"] && firstLoad && this.loadFromUrl ?
                router.currentRoute.query["searchTerm"] :
                this.tableControlModel.searchTerm;

            this.loadFromUrl = this.tab === router.currentRoute.query["tab"];

            if (!firstLoad && this.loadFromUrl) {
                router.replace({ query: {
                    ...this.$route.query,
                    sortOrder: sortOrder.toString(),
                    sortByAsc: sortByAsc.toString(),
                    searchTerm: searchTerm as string} });
                }
            const packages: PackageSummary[] = await Api.package.getPackageSummaries(this.id,
                this.toPackageSummarySortOrder(sortOrder),
                toDomainSortBy(sortByAsc),
                searchTerm,
                skip,
                take);

            this.packageSummaries = skip > 0 ? [...this.packageSummaries, ...packages] : packages;
        } finally {
            this.loadingOrderBy = false;
            this.loadingMore = false;
        }
    }

    private toPackageSummarySortOrder(key: keyof (PackageSummary)): PackageSortOrder {
        switch (key) {
            case "ssccNumber":
                return PackageSortOrder.SsccNumber;
            case "packageGroupSsccNumber":
                return PackageSortOrder.PackageGroup;
            case "lineItemCount":
                return PackageSortOrder.Quantity;
            case "deliveryTimeUtc":
                return PackageSortOrder.DeliveryDate;
            case "warehouseArea":
                return PackageSortOrder.WarehouseArea;
            case "warehouse":
                return PackageSortOrder.Warehouse;
            case "status":
                return PackageSortOrder.Status;
            case "packageType":
                return PackageSortOrder.PackageType;
        }
    }

    private async loadMore(done: () => void) {
        await this.load(this.tableContent.length);
        done();
    }

    private get tableContent(): object[] {
        return this.packageSummaries.map((d: PackageSummary) => ({
            ...d,
            deliveryTimeUtc: dateTimeFilter(d.deliveryTimeUtc),
            status: statusToDisplayNameAndColor(d.status)
        }));
    }

    private get toolbarItems() {
        return [ToolbarItem.TextSearch];
    }
}
